import * as LocalAuthentication from "expo-local-authentication";
import { SecurityLevel } from "expo-local-authentication";
import { Stack } from "expo-router";
import { useEffect, useState } from "react";
import { Image, KeyboardAvoidingView, Platform, StyleSheet } from "react-native";
import { Dialog } from "react-native-paper";
import { Button } from "~/components/Button";
import { AlertModal } from "~/components/feedback/AlertModal";
import { PasscodeInput } from "~/components/form/auth/PasscodeInput";
import { Page } from "~/components/layout/Page";
import { Subtitle } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { Only } from "~/components/permission/Only";
import { Gap } from "~/constants/layout";
import { Radius, Space } from "~/constants/sizing";
import { attemptBiometrics, type PasscodeString, useSession } from "~/providers/SessionProvider";
import { useTheme } from "~/providers/ThemeProvider";

const MAX_ATTEMPTS = 5;

export function Verify() {
    const { colours, border } = useTheme();
    const { hydrated, logged_in, active, authorize, logOut, enrolled, checkPasscode } = useSession();
    const [attempts, setAttempts] = useState(MAX_ATTEMPTS);
    const [level, setLevel] = useState(enrolled);
    const [attempting, setAttempting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [forgot, setForgot] = useState(false);

    const lockout = attempts <= 0 && level === SecurityLevel.SECRET;
    useEffect(() => {
        if (level > SecurityLevel.NONE) {
            setAttempts(MAX_ATTEMPTS);
        }
    }, [level]);

    const attemptFailed = (error?: string) => {
        if (attempts > 0) {
            setAttempts(attempts - 1);
            setError(error ?? (level === SecurityLevel.SECRET ? "Code is not valid." : "Could not verify."));
        } else if (level >= SecurityLevel.BIOMETRIC_WEAK) {
            setLevel(SecurityLevel.SECRET);
            setError("You have made too many attempts. Please use your app passcode.");
        } else {
            setError("You have made too many attempts.");
            setForgot(true);
        }
    };

    const cancelAttempt = () => {
        setAttempting(false);
    };

    const attemptSuccess = () => {
        authorize();
        setAttempts(0);
        setLevel(enrolled);
        setError(null);
        setAttempting(false);
    };

    const makeAttempt = () => {
        if (hydrated && enrolled && !attempting && !lockout) {
            setAttempting(level >= SecurityLevel.BIOMETRIC_WEAK);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(attemptSuccess): Action
    // biome-ignore lint/correctness/useExhaustiveDependencies(attemptFailed): Action
    // biome-ignore lint/correctness/useExhaustiveDependencies(cancelAttempt): Action
    useEffect(() => {
        if (attempting) {
            if (error) {
                setError(null);
            } else {
                attemptBiometrics({
                    promptMessage: "Unlock QENDO",
                    onSuccess: attemptSuccess,
                    onCancel: cancelAttempt,
                    onError: attemptFailed,
                }).finally(cancelAttempt);
            }
        }
    }, [attempting, error]);

    const attemptCode = (code: PasscodeString) => {
        if (lockout) {
            attemptFailed("You cannot try again.");
        } else if (checkPasscode(code)) {
            attemptSuccess();
        } else {
            attemptFailed("Invalid code");
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(attemptSuccess): Action
    useEffect(() => {
        if (logged_in && !active && enrolled === LocalAuthentication.SecurityLevel.NONE) {
            attemptSuccess();
        }
    }, [logged_in, active, enrolled]);

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <Page gradient showVersion>
                <KeyboardAvoidingView
                    style={styles.container}
                    enabled={level === SecurityLevel.SECRET}
                    behavior={Platform.OS === "ios" ? "padding" : "height"}
                >
                    <Image source={require("~/assets/title-logo.png")} style={styles.image} alt="QENDO" />
                    <Only when={level > SecurityLevel.SECRET}>
                        <Subtitle align="center">Unlock app with biometrics</Subtitle>
                    </Only>
                    <Only when={level > SecurityLevel.SECRET}>
                        <Only when={error}>
                            <ErrorMessage size="lg" align="center">
                                {error}
                            </ErrorMessage>
                        </Only>
                        <Button
                            icon="lock"
                            variant="neutral-quiet"
                            iconSize={80}
                            onPress={makeAttempt}
                            disabled={lockout}
                            innerStyle={[styles.unlock, border.tertiary]}
                            colour={colours.icon.tertiary}
                        />
                        <Button title="Use passcode" variant="quiet" onPress={() => setLevel(SecurityLevel.SECRET)} />
                    </Only>
                    <Only when={level === SecurityLevel.SECRET}>
                        <PasscodeInput
                            error={error}
                            onSubmit={attemptCode}
                            onChange={() => setError(null)}
                            attempts={attempts < MAX_ATTEMPTS ? attempts : null}
                        />
                        <Button title="Forgot your passcode?" variant="quiet" onPress={() => setForgot(true)} />
                    </Only>
                </KeyboardAvoidingView>
                {forgot && (
                    <AlertModal
                        icon={lockout ? "warning-outline" : "info-circle"}
                        iconColour={colours.icon.danger}
                        title={lockout ? "Sorry, you've reached the passcode attempt limit" : "Forgot your passcode?"}
                        description="To access your account, please sign in."
                    >
                        <Dialog.Actions style={Gap.md}>
                            <Only not when={lockout}>
                                <Button title="Try again" onPress={() => setForgot(false)} variant="quiet" size="lg" />
                            </Only>
                            <Button title="Go to sign in" onPress={() => logOut(true)} variant="primary" size="lg" />
                        </Dialog.Actions>
                    </AlertModal>
                )}
            </Page>
        </>
    );
}

const styles = StyleSheet.create({
    image: {
        width: 220,
        height: 182,
    },
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        gap: Space.xl,
        zIndex: 9000,
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "transparent",
    },
    unlock: {
        borderStyle: "dashed",
        borderWidth: 3,
        paddingHorizontal: Space.lg,
        paddingVertical: Space.lg,
        borderRadius: Radius.lg,
    },
});
